// v10.5 / ADR-0072 §1, ADR-0075 §2 (V105-3-1, 3-2, 3-3): resolve an inventory clue to a
// product ref (`cloudflare/d1`). Registry-only and deterministic, like provider-signals.ts:
//   - exact match on the clue's kind first; a domain also matches by subdomain
//     (`api.resend.com` → alias `resend.com`), a ci_config by path prefix;
//   - a product alias beats a provider alias of equal or lower specificity;
//   - two different refs at the best specificity → no match (never a guess);
//   - a provider-level match on a provider with exactly ONE product resolves to that
//     product (`npm:resend` → `resend/email`); otherwise it stays `<provider>/*`.
// Pure data access; no runtime imports.
import { getProvider, PROVIDERS } from './providers.js';
let FLAT = null;
function flat() {
    if (FLAT)
        return FLAT;
    const out = [];
    for (const p of PROVIDERS) {
        for (const a of p.aliases)
            out.push({ kind: a.kind, value: a.value.toLowerCase(), provider_key: p.key, product_id: '*', confidence: a.confidence });
        for (const x of p.products ?? [])
            for (const a of x.aliases)
                out.push({ kind: a.kind, value: a.value.toLowerCase(), provider_key: p.key, product_id: x.id, confidence: a.confidence });
    }
    FLAT = out;
    return out;
}
export function normalizeClue(kind, value) {
    const v = value.trim();
    if (kind === 'env_var')
        return v.toUpperCase();
    const l = v.toLowerCase();
    if (kind === 'domain')
        return l.replace(/^[a-z]+:\/\//, '').replace(/[/:?#].*$/, '').replace(/^www\./, '').replace(/\.$/, '');
    if (kind === 'ci_config')
        return l.replace(/\\/g, '/').replace(/^\.\//, '');
    return l;
}
function specificity(kind, alias, value) {
    if (value === alias)
        return 10_000 + alias.length;
    if (kind === 'domain' && value.endsWith(`.${alias}`))
        return alias.length;
    if (kind === 'ci_config' && value.startsWith(`${alias}/`))
        return 5_000 + alias.length;
    return 0;
}
/** Resolve one clue; null when unknown or ambiguous. */
export function resolveProductClue(kind, raw) {
    const value = normalizeClue(kind, raw);
    if (!value)
        return null;
    const aliasValue = (a) => (kind === 'env_var' ? a.value.toUpperCase() : a.value);
    let best = null;
    let tie = false;
    for (const a of flat()) {
        if (a.kind !== kind)
            continue;
        // A product alias outranks a provider alias at the same specificity (+0.5).
        const s = specificity(kind, aliasValue(a), value);
        if (!s)
            continue;
        const score = s + (a.product_id === '*' ? 0 : 0.5);
        if (!best || score > best.score) {
            best = { a, score };
            tie = false;
            continue;
        }
        if (score === best.score && (a.provider_key !== best.a.provider_key || a.product_id !== best.a.product_id))
            tie = true;
    }
    if (!best || tie)
        return null;
    const { provider_key, product_id } = soleProduct(best.a.provider_key, best.a.product_id);
    return { provider_key, product_id, matched: best.a.value, confidence: best.a.confidence, product_level: best.a.product_id !== '*' };
}
/** `<provider>/*` → the provider's only product, when it has exactly one. */
export function soleProduct(providerKey, productId) {
    if (productId !== '*')
        return { provider_key: providerKey, product_id: productId };
    const products = getProvider(providerKey)?.products ?? [];
    return products.length === 1 ? { provider_key: providerKey, product_id: products[0].id } : { provider_key: providerKey, product_id: '*' };
}
/** Display name: "Cloudflare D1", "Resend Transactional email", "GitHub". */
export function productDisplayName(ref) {
    const p = getProvider(ref.provider_key);
    if (!p)
        return ref.provider_key;
    if (ref.product_id === '*')
        return p.name;
    const x = p.products?.find((y) => y.id === ref.product_id);
    if (!x)
        return `${p.name} ${ref.product_id}`;
    // Hyperscaler product names already carry the brand ("Amazon S3", "Cloud SQL").
    if (p.category === 'cloud' || x.name.toLowerCase().startsWith(p.name.toLowerCase()))
        return x.name;
    return `${p.name} ${x.name}`;
}
//# sourceMappingURL=product-clues.js.map